import React from 'react';
import { useTranslation } from 'react-i18next';

interface GradeSliderProps {
  grade: number | null;
  onChange: (grade: number | null) => void;
  disabled?: boolean;
}

const GradeSlider: React.FC<GradeSliderProps> = ({ grade, onChange, disabled = false }) => {
  const { t } = useTranslation();

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    onChange(value);
  };

  const handleClearGrade = () => {
    onChange(null);
  };

  const getGradeDescription = (gradeValue: number | null): string => {
    if (gradeValue === null) return t('grade.not_set');
    
    if (gradeValue >= 10.0) return t('grade.gem_mint');
    if (gradeValue >= 9.5) return t('grade.mint');
    if (gradeValue >= 9.0) return t('grade.near_mint_plus');
    if (gradeValue >= 8.5) return t('grade.near_mint');
    if (gradeValue >= 8.0) return t('grade.near_mint_minus');
    if (gradeValue >= 7.5) return t('grade.very_fine_plus');
    if (gradeValue >= 7.0) return t('grade.very_fine');
    if (gradeValue >= 6.5) return t('grade.very_fine_minus');
    if (gradeValue >= 6.0) return t('grade.fine_plus');
    if (gradeValue >= 5.5) return t('grade.fine');
    if (gradeValue >= 5.0) return t('grade.fine_minus');
    if (gradeValue >= 4.5) return t('grade.very_good_plus');
    if (gradeValue >= 4.0) return t('grade.very_good');
    if (gradeValue >= 3.5) return t('grade.very_good_minus');
    if (gradeValue >= 3.0) return t('grade.good_plus');
    if (gradeValue >= 2.5) return t('grade.good');
    if (gradeValue >= 2.0) return t('grade.good_minus');
    if (gradeValue >= 1.5) return t('grade.fair_plus');
    if (gradeValue >= 1.0) return t('grade.fair');
    return t('grade.poor');
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('grade.label')}
        </label>
        {grade !== null && (
          <button
            type="button"
            onClick={handleClearGrade}
            disabled={disabled}
            className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
          >
            {t('grade.clear')}
          </button>
        )}
      </div>
      
      <div className="space-y-2">
        <input
          type="range"
          min="0.5"
          max="10"
          step="0.5"
          value={grade || 5.0}
          onChange={handleSliderChange}
          disabled={disabled}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 slider"
        />
        
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>0.5</span>
          <span>10.0</span>
        </div>
        
        <div className="text-center">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {grade !== null ? `${grade.toFixed(1)} - ${getGradeDescription(grade)}` : t('grade.not_set')}
          </div>
        </div>
      </div>
      
      {grade !== null && (
        <div className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed mt-2">
          {t(`grade.detailed_${grade.toFixed(1).replace('.', '_')}`)}
        </div>
      )}
    </div>
  );
};

export default GradeSlider;